import {
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { Card, Col, Row, Skeleton, Space, Typography } from 'antd'
import dayjs from 'dayjs'
import { formatInteger, formatPrice } from '@horizon/i18n'
import { supabaseClient } from '@/lib/supabase'
import { useAdminLocale, useAdminTranslate } from '@/providers/i18n-provider'
import type { Locale } from '@/types'

export type InsightModel =
  | 'users'
  | 'content'
  | 'subjects'
  | 'transactions'
  | 'plans'
  | 'support'
  | 'audit'
  | 'administrators'
  | 'ingest'

interface InsightCard {
  metric_key: string
  label: string
  value: number | string
  display: 'number' | 'currency' | 'hours'
  detail: string
  tone: 'positive' | 'neutral' | 'warning' | 'critical'
}

const toneIcon = {
  positive: <CheckCircleOutlined />,
  neutral: <InfoCircleOutlined />,
  warning: <WarningOutlined />,
  critical: <ExclamationCircleOutlined />,
}

const formatValue = (
  card: InsightCard,
  locale: Locale,
  translate: ReturnType<typeof useAdminTranslate>,
) => {
  const value = Number(card.value ?? 0)
  if (card.display === 'currency') return formatPrice(value, locale)
  if (card.display === 'hours') {
    const hours = new Intl.NumberFormat(locale === 'ar' ? 'ar' : 'fr-FR', {
      maximumFractionDigits: 1,
    }).format(value)
    return translate('admin.insights.hoursValue', { value: hours })
  }
  return formatInteger(value)
}

export const ModelInsights = ({ model }: { model: InsightModel }) => {
  const translate = useAdminTranslate()
  const locale = useAdminLocale()
  const to = dayjs().endOf('day')
  const from = to.subtract(29, 'day').startOf('day')
  const insightsQuery = useQuery({
    queryKey: ['admin-model-insights', model, from.format('YYYY-MM-DD')],
    queryFn: async () => {
      const { data, error } = await supabaseClient.rpc('admin_model_insights', {
        p_model: model,
        p_from: from.toISOString(),
        p_to: to.add(1, 'millisecond').toISOString(),
      })
      if (error) throw error
      return (data ?? []) as InsightCard[]
    },
    staleTime: 60_000,
  })

  if (insightsQuery.isError) return null

  return (
    <section className="model-insights" aria-labelledby={`insights-${model}`}>
      <div className="model-insights-heading">
        <div>
          <Typography.Text className="admin-kicker">
            {translate('admin.insights.period')}
          </Typography.Text>
          <Typography.Title level={4} id={`insights-${model}`}>
            {translate(`admin.insights.${model}.title`)}
          </Typography.Title>
          <Typography.Text type="secondary">
            {translate(`admin.insights.${model}.description`)}
          </Typography.Text>
        </div>
      </div>

      <Row gutter={[12, 12]}>
        {insightsQuery.isLoading
          ? Array.from({ length: 4 }, (_, index) => (
              <Col key={index} xs={24} sm={12} xl={6}>
                <Card className="model-insight-card">
                  <Skeleton active paragraph={{ rows: 1 }} />
                </Card>
              </Col>
            ))
          : insightsQuery.data?.map(card => (
              <Col key={card.metric_key} xs={24} sm={12} xl={6}>
                <Card className={`model-insight-card model-insight-card--${card.tone}`}>
                  <Space direction="vertical" size={5} style={{ width: '100%' }}>
                    <div className="model-insight-card-topline">
                      <Typography.Text>
                        {translate(
                          `admin.insights.${model}.${card.metric_key}.label`,
                          undefined,
                          card.label,
                        )}
                      </Typography.Text>
                      <span className="model-insight-card-icon" aria-hidden="true">
                        {toneIcon[card.tone]}
                      </span>
                    </div>
                    <Typography.Title level={3}>
                      {formatValue(card, locale, translate)}
                    </Typography.Title>
                    <Typography.Text type="secondary">
                      {translate(
                        `admin.insights.${model}.${card.metric_key}.detail`,
                        undefined,
                        card.detail,
                      )}
                    </Typography.Text>
                  </Space>
                </Card>
              </Col>
            ))}
      </Row>
    </section>
  )
}
